import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapPin, Plus, Trash2, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { savePracticeData } from '../../../redux/practice/practiceSlice';
import { RootState } from '../../../redux';
import type { Practice, PracticeLocation } from '../../../types';
import { US_STATES_OPTIONS } from '../../../utils/usStates';
import {
  emptyPracticeLocation,
  formatPracticeLocationOneLine,
  isPracticeLocationEmpty,
  normalizePracticeLocations,
  sanitizePracticeLocation,
  validateUSPracticeLocation,
  type LocationFieldErrors,
} from '../../../utils/practiceLocations';

/**
 * Practice Settings — Locations tab.
 * Persists `locations` as PracticeLocation[] (US addresses) on the practice document.
 */
export const LocationsSettings: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedPractice: _selectedPractice, practice: _practice, isLoading, error } = useSelector((state: RootState) => state.practices);
  // Use whichever field is populated — selectedPractice is set after fetch/save,
  // practice is set after login/dashboard load
  const selectedPractice = _selectedPractice ?? _practice ?? null;

  const [locations, setLocations] = useState<PracticeLocation[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<number, LocationFieldErrors>>({});

  useEffect(() => {
    if (selectedPractice?.locations && Array.isArray(selectedPractice.locations)) {
      const normalized = normalizePracticeLocations(selectedPractice.locations);
      setLocations(normalized.length ? normalized : []);
    } else {
      setLocations([]);
    }
    setLocalError(null);
    setFieldErrors({});
  }, [selectedPractice]);

  const addLocation = useCallback(() => {
    setLocations((prev) => [...prev, emptyPracticeLocation()]);
    setLocalError(null);
  }, []);

  const removeAt = useCallback((index: number) => {
    setLocations((prev) => prev.filter((_, i) => i !== index));
    setFieldErrors({});
  }, []);

  const updateLoc = useCallback((index: number, patch: Partial<PracticeLocation>) => {
    setLocations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
    setFieldErrors((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!selectedPractice) {
      setLocalError('No practice loaded. Please refresh or sign in again.');
      return;
    }

    const practiceId = selectedPractice.id || selectedPractice._id || (selectedPractice as any)._id;
    if (!practiceId) {
      setLocalError('Practice ID is missing.');
      return;
    }

    const nonEmpty = locations.filter((l) => !isPracticeLocationEmpty(l));
    const nextErrors: Record<number, LocationFieldErrors> = {};
    let hasErr = false;

    locations.forEach((loc, index) => {
      if (isPracticeLocationEmpty(loc)) return;
      const sanitized = sanitizePracticeLocation(loc);
      const { valid, errors } = validateUSPracticeLocation(sanitized);
      if (!valid) {
        nextErrors[index] = errors;
        hasErr = true;
      }
    });

    setFieldErrors(nextErrors);
    if (hasErr) {
      setLocalError('Fix the highlighted fields before saving.');
      return;
    }

    const cleaned: PracticeLocation[] = nonEmpty.map((l) => {
      const s = sanitizePracticeLocation(l);
      return {
        address1: s.address1,
        ...(s.address2 ? { address2: s.address2 } : {}),
        city: s.city,
        state: s.state,
        zipCode: s.zipCode,
      };
    });

    setIsSaving(true);
    setLocalError(null);
    try {
      const updated: Practice = {
        ...selectedPractice,
        id: String(practiceId),
        locations: cleaned,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await dispatch(savePracticeData(updated) as any);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (e) {
      console.error(e);
      setLocalError('Failed to save locations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading practice…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-red-800 font-medium">Error</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!selectedPractice) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-800 font-medium">No practice found</p>
          <p className="text-yellow-700 text-sm">We couldn’t load your practice. Check that you’re logged in with a valid practice.</p>
        </div>
      </div>
    );
  }

  const inputCls =
    'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const errCls = 'border-red-400 focus:ring-red-400';

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">Locations saved successfully.</span>
        </div>
      )}

      {localError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{localError}</div>
      )}

      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MapPin className="w-7 h-7 text-blue-600" />
          Practice locations
        </h2>
        <p className="text-gray-600 mt-1">
          Add each US office or in-person site. Address line 2 is optional. All fields use standard US postal formats.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={addLocation}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add location
          </button>
        </div>

        {locations.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center border border-dashed border-gray-200 rounded-lg">
            No locations yet. Click &quot;Add location&quot; to add your first office.
          </p>
        ) : (
          <ul className="space-y-8">
            {locations.map((loc, index) => {
              const fe = fieldErrors[index] || {};
              return (
                <li
                  key={index}
                  className="border border-gray-100 rounded-xl p-4 bg-gray-50/80 space-y-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-800">Location {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeAt(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label={`Remove location ${index + 1}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {!isPracticeLocationEmpty(loc) && (
                    <p className="text-xs text-gray-500">{formatPracticeLocationOneLine(loc)}</p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Address line 1</label>
                      <input
                        type="text"
                        value={loc.address1}
                        onChange={(e) => updateLoc(index, { address1: e.target.value })}
                        className={`${inputCls} ${fe.address1 ? errCls : ''}`}
                        placeholder="Street number and name"
                        autoComplete="street-address"
                      />
                      {fe.address1 && <p className="text-xs text-red-600 mt-0.5">{fe.address1}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Address line 2 <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={loc.address2 || ''}
                        onChange={(e) => updateLoc(index, { address2: e.target.value })}
                        className={`${inputCls} ${fe.address2 ? errCls : ''}`}
                        placeholder="Suite, unit, floor"
                      />
                      {fe.address2 && <p className="text-xs text-red-600 mt-0.5">{fe.address2}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                      <input
                        type="text"
                        value={loc.city}
                        onChange={(e) => updateLoc(index, { city: e.target.value })}
                        className={`${inputCls} ${fe.city ? errCls : ''}`}
                        placeholder="City"
                        autoComplete="address-level2"
                      />
                      {fe.city && <p className="text-xs text-red-600 mt-0.5">{fe.city}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                      <select
                        value={loc.state}
                        onChange={(e) => updateLoc(index, { state: e.target.value })}
                        className={`${inputCls} ${fe.state ? errCls : ''}`}
                        autoComplete="address-level1"
                      >
                        <option value="">Select state</option>
                        {US_STATES_OPTIONS.map((s) => (
                          <option key={s.code} value={s.code}>
                            {s.code} — {s.name}
                          </option>
                        ))}
                      </select>
                      {fe.state && <p className="text-xs text-red-600 mt-0.5">{fe.state}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">ZIP code</label>
                      <input
                        type="text"
                        value={loc.zipCode}
                        onChange={(e) => updateLoc(index, { zipCode: e.target.value })}
                        className={`${inputCls} ${fe.zipCode ? errCls : ''}`}
                        placeholder="12345 or 12345-6789"
                        autoComplete="postal-code"
                      />
                      {fe.zipCode && <p className="text-xs text-red-600 mt-0.5">{fe.zipCode}</p>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="pt-4 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? 'Saving…' : 'Save locations'}
          </button>
        </div>
      </div>
    </div>
  );
};
